import { days, drafts, index, names } from "virtual:puzzles";
import type { Day, DayRef, Level, PuzzleSet } from "./types";
import { gameDate } from "../game/clock";

export { drafts, index, names };

export const setFor = (level: Level): PuzzleSet => (level === "easy" ? "family" : "adult");

export const daysOf = (set: PuzzleSet): Day[] => days.filter((d) => d.set === set).sort((a, b) => a.number - b.number);

export const isPublishable = (d: Day): boolean => d.rounds.every((r) => r.sources.length > 0 && !!r.verified && !r.flags?.length);

/** The player's puzzle date as YYYY-MM-DD. A new puzzle opens at 06:00 local time (src/game/clock.ts). */
export const localDate = (now = new Date()): string => gameDate(now);

/** Where a published day lives. The Worker (worker/index.ts) refuses dates that haven't started anywhere yet. */
export const dayUrl = (r: DayRef): string => `/days/${r.set}/${r.date}.json`;

/** Scheduled days of a set up to `today`, newest first. */
export function openRefs(refs: readonly DayRef[], set: PuzzleSet, today: string): DayRef[] {
  return refs.filter((r) => r.set === set && r.date <= today).sort((a, b) => (a.date < b.date ? 1 : -1));
}

type Fetcher = (url: string) => Promise<Pick<Response, "ok" | "status" | "json">>;

const cache = new Map<string, Day>();

/**
 * Days a player can open.
 * - Production: today's day for the player's local date, or the latest one before it
 *   (catch-up window is an open decision — see MECHANICS.md). The bundle holds only the
 *   dates; the day itself is fetched from /days/<set>/<date>.json, so future days can't be
 *   read from the site. If the server refuses a date (a device clock set ahead), the
 *   previous day is tried.
 * - With drafts (dev/e2e/playtest): every day in the set, from the bundle.
 */
export async function playableDays(set: PuzzleSet, today = localDate(), get: Fetcher = (u) => fetch(u)): Promise<Day[]> {
  if (drafts) return daysOf(set);
  for (const ref of openRefs(index, set, today).slice(0, 2)) {
    const url = dayUrl(ref);
    const hit = cache.get(url);
    if (hit) return [hit];
    const res = await get(url);
    if (res.status === 404) continue;
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    const day = (await res.json()) as Day;
    cache.set(url, day);
    return [day];
  }
  return [];
}

/**
 * One scheduled day, for the Vault (a past day) or a challenge link to one. Drafts builds have every
 * day in the bundle; production fetches its file (served only once its date has started).
 */
export async function fetchDay(ref: DayRef, get: Fetcher = (u) => fetch(u)): Promise<Day> {
  if (drafts) {
    const d = daysOf(ref.set).find((x) => x.number === ref.number);
    if (d) return d;
    throw new Error(`no day ${ref.set} #${ref.number}`);
  }
  const url = dayUrl(ref);
  const hit = cache.get(url);
  if (hit) return hit;
  const res = await get(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  const day = (await res.json()) as Day;
  cache.set(url, day);
  return day;
}
